import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronDown, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Appointment } from '../types';
import { formatAppointmentStatusLabel, getAppointmentStartIso, getTotalPrice, isPendingDepositOutstanding } from '../utils/appointmentHelpers';
import LoadingSpinner from './LoadingSpinner';

interface CustomerAppointmentsHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  customerId: string;
  customerName: string;
  currentAppointmentId?: string;
}

const formatDateOnly = (dateString: string | null | undefined) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatTimeOnly = (dateString: string | null | undefined) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? dateString : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getAppointmentDateTimeLabel = (apt: Appointment): string => {
  if (apt.scheduledAt) {
    return `${formatDateOnly(apt.scheduledAt)} · ${formatTimeOnly(apt.scheduledAt)}`;
  }
  const flex = apt.flexScheduledAt;
  if (flex?.earliest && flex?.latest) {
    return `${formatDateOnly(flex.earliest)} · ${formatTimeOnly(flex.earliest)} – ${formatTimeOnly(flex.latest)}`;
  }
  const iso = getAppointmentStartIso(apt);
  if (!iso) return 'Date TBD';
  return `${formatDateOnly(iso)} · ${formatTimeOnly(iso)}`;
};

const getStatusBadgeClass = (status: string) => {
  if (status === 'CONFIRMED') return 'bg-green-50 text-green-800 border-green-200';
  if (status === 'PENDING' || status === 'PENDING_QUOTE') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'COMPLETED') return 'bg-blue-50 text-blue-800 border-blue-200';
  if (status === 'CANCELLED') return 'bg-red-50 text-red-800 border-red-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

const formatPrice = (value: unknown) => {
  const amount = typeof value === 'number' ? value : parseFloat(String(value ?? 0)) || 0;
  return `$${amount.toFixed(2)}`;
};

const getProfessionalName = (assignment: Appointment['assignments'][number]) => {
  const name = [assignment.professionalFirstName, assignment.professionalLastName].filter(Boolean).join(' ').trim();
  return name || 'Unassigned';
};

const getAppointmentSortMs = (apt: Appointment): number => {
  const iso = getAppointmentStartIso(apt);
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

type StatusFilter = 'all' | Appointment['status'] | 'PENDING_DEPOSIT';

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PENDING_QUOTE', label: 'Pending Quote' },
  { value: 'PENDING_DEPOSIT', label: 'Pending Deposit' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Canceled' },
];

const matchesStatusFilter = (apt: Appointment, filter: StatusFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'PENDING_DEPOSIT') return isPendingDepositOutstanding(apt);
  return apt.status === filter;
};

const appointmentMatchesSearch = (apt: Appointment, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const parts: string[] = [];
  if (apt.notes) parts.push(apt.notes);
  parts.push(getAppointmentDateTimeLabel(apt));

  const addDateParts = (iso: string | null | undefined) => {
    if (!iso) return;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return;
    parts.push(formatDateOnly(iso), formatTimeOnly(iso));
    parts.push(d.toLocaleDateString('en-US'));
    parts.push(d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    parts.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
    parts.push(d.toISOString().slice(0, 10));
    parts.push(formatDateOnly(iso).replace(/\D/g, ''));
  };
  addDateParts(getAppointmentStartIso(apt));
  addDateParts(apt.flexScheduledAt?.latest);

  const addPriceParts = (value: unknown) => {
    const amount = typeof value === 'number' ? value : parseFloat(String(value ?? 0)) || 0;
    parts.push(String(amount), amount.toFixed(2), `$${amount.toFixed(2)}`, `$${amount}`);
  };
  addPriceParts(getTotalPrice(apt));

  for (const assignment of apt.assignments || []) {
    if (assignment.serviceName) parts.push(assignment.serviceName);
    parts.push(getProfessionalName(assignment));
    addPriceParts(assignment.price);
  }

  return parts.join(' ').toLowerCase().includes(q);
};

const getAssignmentIcon = (assignment: Appointment['assignments'][number]): string | null => {
  const icon =
    assignment.serviceIcon ||
    (assignment as any).icon ||
    (assignment as any).categoryIcon ||
    (assignment as any).category_icon ||
    (assignment as any).category?.icon;
  if (typeof icon === 'string' && icon.trim() !== '') return icon;
  return null;
};

const CustomerAppointmentsHistoryModal: React.FC<CustomerAppointmentsHistoryModalProps> = ({
  isOpen,
  onClose,
  businessId,
  customerId,
  customerName,
  currentAppointmentId,
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [totalVisits, setTotalVisits] = useState(0);
  const [appointmentStats, setAppointmentStats] = useState({ noShows: 0, lateCanceledRescheduled: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen || !businessId || !customerId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setStatsLoading(true);
      setExpandedIds(new Set());
      try {
        const [first, stats] = await Promise.all([
          apiService.getAppointments({
            businessId,
            customerId,
            page: 1,
            limit: 100,
            sort: 'desc',
          }),
          apiService.getAppointmentStats({ businessId, customerId }).catch(() => ({
            noShows: 0,
            lateCanceledRescheduled: 0,
          })),
        ]);
        let all = first.data || [];
        const totalPages = first.pagination?.totalPages || 1;
        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              apiService.getAppointments({
                businessId,
                customerId,
                page: i + 2,
                limit: 100,
                sort: 'desc',
              })
            )
          );
          all = all.concat(rest.flatMap((page) => page.data || []));
        }
        if (!cancelled) {
          setAppointments(all);
          setTotalVisits(first.pagination?.total ?? all.length);
          setAppointmentStats({
            noShows: Number(stats?.noShows) || 0,
            lateCanceledRescheduled: Number(stats?.lateCanceledRescheduled) || 0,
          });
        }
      } catch (error: any) {
        console.error('Error loading customer appointments:', error);
        if (!cancelled) {
          setAppointments([]);
          setTotalVisits(0);
          setAppointmentStats({ noShows: 0, lateCanceledRescheduled: 0 });
          toast.error(error?.response?.data?.message || 'Failed to load customer appointments');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setStatsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId, customerId]);

  useEffect(() => {
    if (!isOpen) return;
    setStatusFilter('all');
    setSearchQuery('');
  }, [isOpen, customerId]);

  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const nowMs = Date.now();
    const upcoming: Appointment[] = [];
    const past: Appointment[] = [];
    for (const apt of appointments) {
      if (!matchesStatusFilter(apt, statusFilter) || !appointmentMatchesSearch(apt, searchQuery)) continue;
      if (getAppointmentSortMs(apt) >= nowMs) upcoming.push(apt);
      else past.push(apt);
    }
    upcoming.sort((a, b) => getAppointmentSortMs(a) - getAppointmentSortMs(b));
    past.sort((a, b) => getAppointmentSortMs(b) - getAppointmentSortMs(a));
    return { upcomingAppointments: upcoming, pastAppointments: past };
  }, [appointments, statusFilter, searchQuery]);
  const hasFilteredResults = upcomingAppointments.length > 0 || pastAppointments.length > 0;
  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  if (!isOpen) return null;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderAppointmentRow = (row: Appointment) => {
    const isCurrent = currentAppointmentId && String(row.id) === String(currentAppointmentId);
    const isExpanded = expandedIds.has(row.id);
    return (
      <li key={row.id}>
        <button
          type="button"
          onClick={() => toggleExpanded(row.id)}
          className={`w-full text-left border px-3 py-3 transition-colors ${
            isExpanded ? 'rounded-t-lg' : 'rounded-lg'
          } ${
            isCurrent
              ? 'border-[#0B4778] bg-[#0B4778]/5 ring-1 ring-[#0B4778]/30'
              : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
          }`}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 min-w-0">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
              )}
              <span className="text-sm font-semibold text-gray-900">
                {getAppointmentDateTimeLabel(row)}
              </span>
            </span>
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${getStatusBadgeClass(row.status)}`}
            >
              {formatAppointmentStatusLabel(row.status, row)}
            </span>
          </div>
          {isCurrent && (
            <div className="text-xs text-[#0B4778] font-medium mt-1 pl-6">Current appointment</div>
          )}
        </button>

        {isExpanded && (
          <div className="border border-t-0 border-gray-200 rounded-b-lg px-3 py-3 bg-white -mt-px">
            {(row.assignments || []).length === 0 ? (
              <p className="text-sm text-gray-500">No services on this appointment.</p>
            ) : (
              <ul className="space-y-2">
                {(row.assignments || []).map((assignment) => (
                  <li
                    key={assignment.id || `${assignment.serviceId}-${assignment.fromDateTime || ''}`}
                    className="flex items-start justify-between gap-3 text-sm"
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <span
                        className="w-6 h-6 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: assignment.categoryBackgroundColor || undefined }}
                      >
                        <span className="text-sm">{getAssignmentIcon(assignment) || '📦'}</span>
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900">
                          {assignment.serviceName || 'Service'}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {getProfessionalName(assignment)}
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-gray-900 flex-shrink-0">
                      {formatPrice(assignment.price)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between pt-2 mt-3 border-t border-gray-200">
              <span className="text-sm font-bold text-gray-900">Total</span>
              <span className="text-sm font-bold font-mono text-gray-900">
                {formatPrice(getTotalPrice(row))}
              </span>
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[90] flex items-center justify-center p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[min(90vh,720px)] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-appointments-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-200 flex-shrink-0 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2
              id="customer-appointments-history-title"
              className="text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-0"
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0B4778]/10 flex-shrink-0">
                <Calendar className="w-5 h-5 text-[#0B4778]" />
              </div>
              <span className="truncate">{customerName ? `${customerName}'s Appointments` : 'Customer Appointments'}</span>
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-calendar2-check text-blue-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-xs font-extrabold uppercase tracking-wider text-gray-600">Total Visits</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {statsLoading ? '…' : totalVisits}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold text-gray-600 whitespace-nowrap">
                  No-Shows:{' '}
                  <span className="text-gray-900 font-bold">
                    {statsLoading ? '…' : appointmentStats.noShows}
                  </span>
                </div>
                <div className="text-[11px] font-semibold text-gray-600 whitespace-nowrap mt-1">
                  Rescheduled:{' '}
                  <span className="text-gray-900 font-bold">
                    {statsLoading ? '…' : appointmentStats.lateCanceledRescheduled}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search appointments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                title="Search by professional, service, notes, price, or date"
                className={`w-full min-w-0 h-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-transparent placeholder:truncate ${searchQuery ? 'pl-10 pr-10' : 'pl-10 pr-4'}`}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Clear search"
                >
                  <i className="bi bi-x text-xl"></i>
                </button>
              )}
            </div>
            <div className="relative sm:w-44">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="w-full h-10 py-2 px-3 pr-10 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-transparent appearance-none"
                aria-label="Filter by status"
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i className="bi bi-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : appointments.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-8">No appointments found for this customer.</p>
          ) : !hasFilteredResults ? (
            <p className="text-sm text-gray-600 text-center py-8">
              {hasActiveFilters ? 'No appointments match your search or filter.' : 'No appointments found for this customer.'}
            </p>
          ) : (
            <div className="space-y-5">
              {upcomingAppointments.length > 0 && (
                <div>
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-600 mb-2">Upcoming</h4>
                  <ul className="space-y-2">
                    {upcomingAppointments.map(renderAppointmentRow)}
                  </ul>
                </div>
              )}
              {pastAppointments.length > 0 && (
                <div>
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-600 mb-2">Past</h4>
                  <ul className="space-y-2">
                    {pastAppointments.map(renderAppointmentRow)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button type="button" onClick={onClose} className="aacore-btn2-secondary">
            <X className="w-4 h-4" />
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerAppointmentsHistoryModal;
